import { ref } from 'vue';
import type { ICoordinates } from '../types/map.ts';

/**
 * Интерфейс для настроек Яндекс.Карт
 */
export interface IMapSettings {
    apiKey: string;
    lang: string;
    coordorder: string;
    version: string;
}

/**
 * Интерфейс для позиции элемента управления
 */
export interface IControlPosition {
    right?: number;
    left?: number;
    top?: number;
    bottom?: number;
}

/**
 * Интерфейс для детальных настроек элементов управления
 */
export interface IDetailedControl {
    position?: IControlPosition;
    size?: 'small' | 'large';
}

/**
 * Константы карты
 */
export const MAP_CONSTANTS = {
    DEFAULT_CENTER: {
        lat: 55.753605,
        lng: 37.619773, // Москва по умолчанию
    },
    DEFAULT_ZOOM: 9,
    SELECTED_ZOOM: 15,
    DETAILED_ZOOM: 17,
    POINT_ZOOM: 14,
} as const;

/**
 * Composable для конфигурации Яндекс.Карт
 * Предоставляет общие настройки для всех карт в приложении
 * 
 * @returns {Object} Объект с настройками карты
 */
export function useMapConfig() {
    /**
     * Основные настройки Яндекс.Карт
     */
    const settings = ref<IMapSettings>({
        apiKey: process.env.YANDEX_MAP_KEY || 'b36b41e6-cd2b-4edd-8bae-96045ae3489e',
        lang: 'ru_RU',
        coordorder: 'latlong',
        version: '2.1',
    });

    /**
     * Список элементов управления на карте
     */
    const controls = ['zoomControl', 'geolocationControl'];

    /**
     * Детальные настройки элементов управления
     */
    const detailedControls: Record<string, IDetailedControl> = {
        zoomControl: {
            position: { right: 10, bottom: 80 },
            size: 'small',
        },
        geolocationControl: {
            position: { right: 10, bottom: 45 },
        },
    };

    /**
     * Опции карты
     */
    const options = ref({
        yandexMapDisablePoiInteractivity: true,
    });

    /**
     * Получить координаты центра по умолчанию
     * Использует местоположение пользователя из конфига или Москву
     * 
     * @returns {[number, number]} Координаты [lat, lng]
     */
    const getDefaultCenter = (): [number, number] => {
        const userConfig = (window as any)?.config?.user?.location;

        if (userConfig?.lat && userConfig?.lng) {
            return [
                parseFloat(userConfig.lat),
                parseFloat(userConfig.lng),
            ];
        }

        return [MAP_CONSTANTS.DEFAULT_CENTER.lat, MAP_CONSTANTS.DEFAULT_CENTER.lng];
    };

    /**
     * Получить координаты в формате объекта
     * 
     * @returns {ICoordinates} Координаты { lat, lng }
     */
    const getDefaultCenterObject = (): ICoordinates => {
        const [lat, lng] = getDefaultCenter();
        return { lat, lng };
    };

    /**
     * Преобразовать координаты из объекта в массив
     * 
     * @param {ICoordinates} coordinates - Координаты в формате { lat, lng }
     * @returns {[number, number]} Координаты [lat, lng]
     */
    const coordinatesToArray = (coordinates: ICoordinates): [number, number] => {
        return [parseFloat(String(coordinates.lat)), parseFloat(String(coordinates.lng))];
    };

    /**
     * Преобразовать координаты из массива в объект
     * 
     * @param {[number, number]} coordinates - Координаты в формате [lat, lng]
     * @returns {ICoordinates} Координаты { lat, lng }
     */
    const coordinatesToObject = (coordinates: [number, number]): ICoordinates => {
        return {
            lat: coordinates[0],
            lng: coordinates[1],
        };
    };

    return {
        settings,
        controls,
        detailedControls,
        options,
        MAP_CONSTANTS,
        getDefaultCenter,
        getDefaultCenterObject,
        coordinatesToArray,
        coordinatesToObject,
    };
}

